<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.reservation.actions.discount.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.apply'), type: 'confirm', callback: confirm, disabled: !canApply },
    ]"
    :portal="portal"
  >
    <div v-if="reservation" class="pt-4 text-left space-y-4">
      <o-alert v-if="!hasAvailableOptions" show variant="info" base-class="rounded p-2 relative flex items-start text-sm">
        {{ $t('components.reservation.actions.discount.noneAvailable') }}
      </o-alert>

      <template v-for="(service, index) in services" :key="index">
        <template v-if="options.services[index]">
          <div v-if="(options.services[index].customerPasses && options.services[index].customerPasses.length) || (options.services[index].vouchers && options.services[index].vouchers.length)">
            <div>
              <p class="text-sm" leading-none>
                {{ service.service.title }}
              </p>
              <p class="opacity-80 text-xs leading-none mb-1">
                {{ service.employee.displayName }}
              </p>
            </div>

            <template v-if="service.idOrder">
              <o-alert show variant="warning" base-class="rounded p-2 relative flex items-start relative' text-sm text-red-700 mb-2">
                {{ $t('components.reservation.actions.discount.alert') }}
              </o-alert>
            </template>

            <template>
              <discount-select
                :service="service"
                :employee="service.employee"
                v-model:customerPass="service.customerPass"
                :customerPasses="service.idOrder ? [] : options.services[index].customerPasses"
                v-model:voucher="service.voucher"
                :vouchers="options.services[index].vouchers"
                :placeholder="service.idOrder ? $t('components.reservation.actions.discount.selectPass') : $t('components.reservation.actions.discount.selectPassOrPromo')"
                class="w-full"
              />
            </template>
          </div>
        </template>
      </template>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import DiscountSelect from '@/components/reservation/elements/DiscountSelect.vue'

import { OAlert } from '@/components/elements'

interface Props {
  reservation?: Record<string, any> | null
  portal?: string | null
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  reservation: null,
  portal: null,
  loading: false,
})

const emit = defineEmits<{
  'update:loading': [value: boolean]
  'update:reservation': [value: any]
}>()

const { $api, $notification, $i18n } = useNuxtApp()

const dialog = ref<any>(null)

const services = ref<any[]>([])
const originalPasses = ref<any>(null)
const originalVouchers = ref<any>(null)

const info = computed(() => {
  return $i18n.t('components.reservation.actions.discount.info', { name: props.reservation?.customer?.displayName, label: props.reservation?.customerInfoLabel })
})

const options = computed(() => {
  return props.reservation?.options
})

const changedPasses = computed(() => {
  const customerPasses = services.value
    .map((service: any) => service.customerPass?.idCustomerPass)

  return JSON.stringify(customerPasses) !== JSON.stringify(originalPasses.value)
})

const changedVouchers = computed(() => {
  const vouchers = services.value
    .map((service: any) => service.voucher?.idVoucher)

  return JSON.stringify(vouchers) !== JSON.stringify(originalVouchers.value)
})

const hasAvailableOptions = computed(() => {
  return options.value?.services?.some((service: any) => service?.customerPasses?.length || service?.vouchers?.length) ?? false
})

const canApply = computed(() => {
  return changedPasses.value || changedVouchers.value
})

function open() {
  dialog.value.open()

  services.value = props.reservation!.services
    .map((service: any) => toRaw(service).clone())

  originalPasses.value = services.value
    .map((service: any) => service.customerPass?.idCustomerPass)

  originalVouchers.value = services.value
    .map((service: any) => service.voucher?.idVoucher)
}

async function confirm() {
  emit('update:loading', true)

  const servicesVal = services.value

  const customerPasses = servicesVal
    .map((service: any) => service.customerPass?.idCustomerPass)

  const vouchers = servicesVal
    .map((service: any) => service.voucher?.idVoucher)

  try {
    let reservation
    const reservationId = props.reservation?.idReservation?.toString()

    if (changedPasses.value) {
      reservation = (await $api.reservation.assignCustomerPasses(reservationId, customerPasses)).data
    }

    if (changedVouchers.value) {
      reservation = (await $api.reservation.applyVouchers(reservationId, vouchers)).data
    }

    if (reservation) {
      emit('update:reservation', reservation)
      emit('update:loading', false)


      let success = 'discount.success'
      let successInfo = 'discount.successInfo'

      if (changedPasses.value && changedVouchers.value) {
        success = 'components.reservation.actions.discount.passPromoSuccess'
        successInfo = 'components.reservation.actions.discount.passPromoSuccessInfo'
      } else if (changedPasses.value) {
        success = 'components.reservation.actions.discount.passSuccess'
        successInfo = 'components.reservation.actions.discount.passSuccessInfo'
      } else if (changedVouchers.value) {
        success = 'components.reservation.actions.discount.promoSuccess'
        successInfo = 'components.reservation.actions.discount.promoSuccessInfo'
      }

      $notification.toast({
        title: $i18n.t(success),
        text: $i18n.t(successInfo, { name: props.reservation!.customer?.displayName, label: props.reservation!.customerInfoLabel }),
        variant: 'success',
      })

      return true
    }
  } catch (e: any) {
    console.log(e)

    $notification.error({ error: e?.errors })
  }
}

defineExpose({ open })
</script>
